import React, {useEffect, useRef, useState} from "react";
import {useDropzone} from "react-dropzone";
import makeStyles from "@material-ui/core/styles/makeStyles";
import {read, utils, writeFile} from "xlsx";
import {
    Button,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    MenuItem,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@material-ui/core";
import api from "../../services/api";
import upload from "../../assets/upload.gif";
import {useHistory} from "react-router-dom";
import toastError from "../../errors/toastError";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import {toast} from "react-toastify";
import {i18n} from "../../translate/i18n";
import {extractIsoCode} from "../../utils/countries";

function WorksheetToDatagrid(ws) {
    /* create an array of arrays - mantendo formatação de células */
    const rows = utils.sheet_to_json(ws, {header: 1, defval: "", raw: false});

    /* create column array */
    const range = utils.decode_range(ws["!ref"] || "A1");
    const columns = Array.from({length: range.e.c + 1}, (_, i) => ({
        key: String(i), // RDG will access row["0"], row["1"], etc
        name: utils.encode_col(i), // the column labels will be A, B, etc
        //editor: textEditor // enable cell editing
    }));

    return {rows, columns}; // these can be fed to setRows / setColumns
}


const useStyles = makeStyles((theme) => ({
    xlsTable: {
        width: "100%",
    },
    tableContainer: {
        flex: 1,
        padding: 1,
        overflowX: "auto",
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    actions: {
        padding: 2,
        border: "1px solid #CCC",
        boxShadow: "1px 1px 5px #CCC",
        marginTop: 2,
        display: "flex",
        justifyContent: "center",
    },
    importOptions: {
        padding: 2,
        border: "1px solid #CCC",
        boxShadow: "1px 1px 5px #CCC",
        marginTop: 2,
        marginBottom: 2,
    },
    error: {
        color: "red",
        marginTop: 1,
    },
    buttonImport: {
        marginRight: 1,
    },
    select: {
        minWidth: 200,
    },
    backButtonContainer: {
        textAlign: "center",
        marginTop: 20,
    },
}));

const ContactImport = () => {
    const size = useWindowDimensions();

    const [rows, setRows] = useState(null);
    const [columns, setColumns] = useState(null);
    const classes = useStyles();
    const history = useHistory();
    const [contactFieldsAvailable, setContactFieldsAvailable] = useState([]);
    const [columnValue, setColumnValue] = useState({});
    const [selectedFields, setSelectedFields] = useState({}); // Para rastrear seleções únicas

    const [openingFile, setOpeningFile] = useState(false);
    const [selection, setSelection] = useState({});
    const [invalidFile, setInvalidFile] = useState(false);
    const [error, setError] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [selectedRows, setSelectedRows] = useState({});
    const [validateContact, setValidateContact] = useState(true);

    // Função para converter número serial do Excel para data JavaScript
    const excelSerialToDate = (serialNumber) => {
        if (!serialNumber || serialNumber < 0) return null;
        
        // Excel armazena datas desde 01/01/1900
        // Serial 1 = 01/01/1900
        // Serial 2 = 02/01/1900
        
        // Diferença em dias entre 01/01/1900 e 01/01/1970 (Unix epoch)
        const daysDifference = 25569;
        
        // Converter serial para Unix timestamp (millisegundos)
        const timestamp = (serialNumber - daysDifference) * 86400000;
        
        // Criar Date object
        const date = new Date(timestamp);
        
        // Verificar se é válido
        if (isNaN(date.getTime())) {
            console.warn('Invalid Excel serial:', serialNumber);
            return null;
        }
        
        return date;
    };

    // Função helper para detectar e corrigir formato de data MM/DD vs DD/MM
    const parseAndFixDateFormat = (dateStr) => {
        if (!dateStr || typeof dateStr !== 'string') return null;
        
        // Processar datas com formato MM/DD/YYYY (Excel americano) e inverter para DD/MM/YYYY
        if (dateStr.includes('/')) {
            const parts = dateStr.split('/');
            if (parts.length === 3) {
                const first = parseInt(parts[0]);
                const second = parseInt(parts[1]);
                const year = parts[2];
                
                // Sempre assumir que é MM/DD/YYYY do Excel e converter para DD/MM/YYYY
                // Excel usa formato americano mesmo quando digitamos DD/MM
                console.log(`📅 Invertendo MM/DD para DD/MM: ${first}/${second}/${year}`);
                
                // Retornar como DD/MM/YYYY (invertendo)
                return new Date(parseInt(year), first - 1, second);
            }
        }
        
        return new Date(dateStr);
    };
    // Colunas de número às vezes chegam como célula "Number" do Excel e, quando a
    // coluna não está formatada como Texto, o Excel pode exibi-las em notação
    // científica (ex: 5.52E+12). Reconstrói o número completo antes de usá-lo.
    const normalizeExcelNumberCell = (value) => {
        if (typeof value === 'number') {
            return value.toFixed(0);
        }
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (/^-?\d+([.,]\d+)?[eE][+-]?\d+$/.test(trimmed)) {
                const parsed = Number(trimmed.replace(',', '.'));
                if (!isNaN(parsed)) {
                    return parsed.toFixed(0);
                }
            }
            return trimmed;
        }
        return String(value ?? '').trim();
    };

    const contactFields = [
        {id: "name", label: "Nome", required: true},
        {id: "number", label: "Número", required: true},
        {id: "country", label: "País", required: true},
        {id: "email", label: "E-mail", required: false},
        {id: "birthDate", label: "Data de Nascimento", required: false},
        {id: "tags", label: "Tags", required: false},
        {id: "carteira", label: "Carteira", required: false},
        {id: "cep", label: "CEP", required: false},
        {id: "endereco", label: "Endereço", required: false},
        {id: "cidade", label: "Cidade", required: false},
        {id: "estado", label: "Estado", required: false},
        {id: "cnpj", label: "CNPJ", required: false},
    ];

    useEffect(() => {
        setContactFieldsAvailable(contactFields);
    }, []);

    const handleDownloadReport = (duplicates, rejected) => {
        const workbook = utils.book_new();

        if (duplicates.length > 0) {
            const duplicatesSheet = utils.json_to_sheet(
                duplicates.map(({name, number}) => ({Nome: name, Número: number}))
            );
            utils.book_append_sheet(workbook, duplicatesSheet, "Duplicados");
        }

        if (rejected.length > 0) {
            const rejectedSheet = utils.json_to_sheet(
                rejected.map(({name, number, reason}) => ({Nome: name, Número: number, Motivo: reason}))
            );
            utils.book_append_sheet(workbook, rejectedSheet, "Não importados");
        }

        writeFile(workbook, "relatorio-importacao.xlsx");
    };

    const processImport = async () => {
        setUploading(true);

        console.log(selection)

        if (!selection.number) {
            toastError("Não foi selecionado o campo de número do contato");
            setUploading(false);
            return;
        }

        if (!selection.name) {
            toastError("Não foi selecionado o campo de nome do contato");
            setUploading(false);
            return;
        }

        if (!selection.country) {
            toastError("Não foi selecionado o campo de país do contato");
            setUploading(false);
            return;
        }

        if (Object.keys(selectedRows).length === 0) {
            toastError("Nenhum contato selecionado");
            setUploading(false);
            return;
        }

        if (rows?.length > 1) {
            let importedCount = 0;
            const duplicates = [];
            const rejected = [];

            for (let index = 1; index < rows.length; index++) {
                if (selectedRows[index]) { // Importar apenas as linhas selecionadas
                    const item = rows[index];
                    const contactData = {};

                    for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
                        const column = columns[columnIndex];
                        const selectedField = columnValue[column.key];

                        if (selectedField) {
                            // Garantir que número seja sempre string
                            if (selectedField === 'number' && item[columnIndex]) {
                                contactData[selectedField] = normalizeExcelNumberCell(item[columnIndex]);
                            } else if (selectedField === 'country' && item[columnIndex]) {
                                // Aceita tanto o código puro ("br") quanto o label com DDI que o
                                // template gera ("(+55) BR") — só o código ISO de 2 letras importa.
                                contactData[selectedField] = extractIsoCode(item[columnIndex]);
                            } else if (selectedField === 'birthDate' && item[columnIndex] !== null && item[columnIndex] !== undefined && item[columnIndex] !== '') {
                                // Processar data de nascimento
                                let dateValue = item[columnIndex];
                                let parsedDate = null;
                                
                                try {
                                    // Excel armazena datas como números seriais (dias desde 30/12/1899)
                                    if (typeof dateValue === 'number' && dateValue > 1 && dateValue < 100000) {
                                        // Converter número serial do Excel para JavaScript Date
                                        parsedDate = excelSerialToDate(dateValue);
                                        console.log('Convertendo Excel serial:', dateValue, 'para data:', parsedDate);
                                    } else if (typeof dateValue === 'string') {
                                        // Processar strings de data
                                        // Usar função helper para detectar e corrigir formato
                                        parsedDate = parseAndFixDateFormat(dateValue);
                                        console.log('📅 Data parseada:', dateValue, '→', parsedDate);
                                    }
                                    
                                    // Validar e converter para formato YYYY-MM-DD
                                    if (parsedDate && !isNaN(parsedDate.getTime()) && parsedDate <= new Date()) {
                                        contactData[selectedField] = parsedDate.toISOString().split('T')[0];
                                        console.log('✅ Data processada com sucesso:', dateValue, '→', contactData[selectedField]);
                                    } else if (parsedDate) {
                                        console.warn('⚠️ Data de nascimento inválida ou futura:', dateValue);
                                    }
                                } catch (error) {
                                    console.warn('❌ Erro ao processar data de nascimento:', dateValue, error);
                                }
                            } else {
                                contactData[selectedField] = item[columnIndex];
                            }
                        }
                    }

                    // Verificar se os campos obrigatórios estão presentes
                    const missingFields = contactFields.filter(field =>
                        field.required && (!contactData[field.id] || contactData[field.id] === '')
                    );

                    if (missingFields.length > 0) {
                        rejected.push({
                            name: contactData.name,
                            number: contactData.number,
                            reason: i18n.t("contacts.importReport.missingFields", {
                                fields: missingFields.map(f => f.label).join(", "),
                            }),
                        });
                        continue;
                    }

                    // Formatar número se necessário
                    if (contactData.number) {
                        // Remover caracteres não numéricos
                        contactData.number = contactData.number.toString().replace(/\D/g, '');

                        // Prefixar DDI 55 só para contatos brasileiros e só quando o número
                        // ainda não o contém (evita duplicar "+55"/"(55)"/etc já presentes e
                        // evita corromper números de outros países, ex: EUA)
                        if (
                            contactData.country === 'BR' &&
                            !contactData.number.startsWith('55') &&
                            contactData.number.length <= 11
                        ) {
                            contactData.number = `55${contactData.number}`;
                        }
                    }

                    try {
                        console.log("Enviando dados para importação:", contactData);
                        const {data} = await api.post('/contactsImport', {
                            ...contactData,
                            validateContact: validateContact ? "true" : "false",
                        });

                        console.log("Resposta da API:", data);

                        if (data?.isDuplicate) {
                            duplicates.push({name: contactData.name, number: contactData.number});
                        } else {
                            importedCount++;
                        }
                    } catch (error) {
                        console.error("Erro na importação:", error);
                        const errorCode = error.response?.data?.error;
                        const reason = errorCode
                            ? (i18n.exists(`backendErrors.${errorCode}`) ? i18n.t(`backendErrors.${errorCode}`) : errorCode)
                            : (error.message || "Erro desconhecido");
                        rejected.push({name: contactData.name, number: contactData.number, reason});
                    }
                }
            }

            setValidateContact(false);
            setSelectedRows({});
            setUploading(false);
            setRows(null);
            setColumns(null);
            setColumnValue({});
            setSelectedFields({});

            const summaryText = i18n.t("contacts.importReport.summary", {
                imported: importedCount,
                duplicates: duplicates.length,
                rejected: rejected.length,
            });

            if (duplicates.length > 0 || rejected.length > 0) {
                toast(
                    () => (
                        <div style={{color: "#1a1a1a"}}>
                            <div>{summaryText}</div>
                            <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                fullWidth
                                style={{marginTop: 8}}
                                onClick={() => handleDownloadReport(duplicates, rejected)}
                            >
                                {i18n.t("contacts.importReport.download")}
                            </Button>
                        </div>
                    ),
                    {autoClose: false}
                );
            } else {
                toast(summaryText);
            }
        }
    };

    const onChangeFile = (acceptedFiles) => {
        if (acceptedFiles.length === 0) return;
        setOpeningFile(true);
        setInvalidFile(false);
        setUploading(false);
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onload = function (e) {
            try {
                const data = e.target.result;
                const wb = read(data);
                // Prefere a aba "Contatos" pelo nome (é o que o template gerado usa) —
                // não confia na ordem/índice, já que o mesmo arquivo pode ter outras
                // abas (ex: a aba oculta de códigos ISO). Cai pra primeira aba se o
                // arquivo não tiver esse nome (planilha antiga ou feita à mão).
                const sheetName = wb.SheetNames.includes("Contatos") ? "Contatos" : wb.SheetNames[0];
                const ws = wb.Sheets[sheetName];
                const {rows, columns} = WorksheetToDatagrid(ws);
                setRows(rows);
                setColumns(columns);
                setOpeningFile(false);
            } catch (e) {
                console.error(e);
                setInvalidFile(true);
                setOpeningFile(false);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleSelectChange = (event) => {
        const newValue = event.target.value;
        const columnKey = event.target.name;

        if (columnValue[columnKey]) {
            const oldValue = columnValue[columnKey];
            setSelectedFields((prevSelectedFields) => {
                const newSelectedFields = {...prevSelectedFields};
                delete newSelectedFields[oldValue];
                return newSelectedFields;
            });
        }

        if (newValue === "") {
            setColumnValue((prevColumnValue) => {
                const newColumnValue = {...prevColumnValue};
                delete newColumnValue[columnKey];
                return newColumnValue;
            });
            setSelection((prevSelection) => {
                const newSelection = {...prevSelection};
                Object.keys(newSelection).forEach((key) => {
                    if (newSelection[key] === columnKey) {
                        delete newSelection[key];
                    }
                });
                return newSelection;
            });
            return;
        }

        if (selectedFields[newValue]) {
            toastError(`O campo ${newValue} já foi selecionado.`);
            return;
        }

        setSelection((selection) => ({...selection, [newValue]: columnKey}));
        setSelectedFields((prevSelectedFields) => ({...prevSelectedFields, [newValue]: columnKey}));
        setColumnValue((columnValue) => ({...columnValue, [columnKey]: newValue}));
    };


    const renderSelectbox = (column) => {
        return (
            <Select value={columnValue[column.key]} name={column.key} onChange={handleSelectChange}>
                <MenuItem value="">&nbsp;</MenuItem>
                {contactFieldsAvailable.map((contactField) => (
                    <MenuItem value={contactField.id}>{contactField.label}</MenuItem>
                ))}
            </Select>
        );
    };

    const renderXls = () => {
        return (
            <TableContainer className={classes.tableContainer} style={{height: size.height * 0.75}}>
                <Table stickyHeader>
                    <TableHead key={columns.length}>
                        <TableRow>
                            <TableCell>
                                <input
                                    type="checkbox"
                                    checked={Object.keys(selectedRows).length === rows.length - 1} // Se todas as linhas, exceto a primeira, estiverem marcadas
                                    onChange={(event) => {
                                        const isChecked = event.target.checked;
                                        const newSelectedRows = {};
                                        if (isChecked) {
                                            // Marcar todas as linhas, exceto a primeira
                                            for (let i = 1; i < rows.length; i++) {
                                                newSelectedRows[i] = true;
                                            }
                                        }
                                        setSelectedRows(newSelectedRows);
                                    }}
                                />
                            </TableCell>
                            {columns.map((column) => (
                                <TableCell key={column.key}>{column.name}</TableCell>
                            ))}
                        </TableRow>
                        <TableRow>
                            <TableCell></TableCell>
                            {columns.map((column) => (
                                <TableCell key={column.key}>{renderSelectbox(column)}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, rowIndex) => (
                            <TableRow key={rowIndex}>
                                {rowIndex !== 0 && (
                                    <TableCell>
                                        <input
                                            type="checkbox"
                                            checked={!!selectedRows[rowIndex]}
                                            onChange={() => {
                                                setSelectedRows((prevSelectedRows) => ({
                                                    ...prevSelectedRows,
                                                    [rowIndex]: !prevSelectedRows[rowIndex],
                                                }));
                                            }}
                                        />
                                    </TableCell>
                                )}
                                {rowIndex !== 0 && (
                                    row.map((column, columnIndex) => {
                                        // Formatar data se a coluna é birthDate
                                        let cellValue = column;
                                        
                                        // Verificar se esta coluna foi mapeada como birthDate
                                        if (columnValue[columnIndex] === 'birthDate' && column) {
                                            try {
                                                // Se for número (Excel serial), converter
                                                if (typeof column === 'number' && column > 1 && column < 100000) {
                                                    const convertedDate = excelSerialToDate(column);
                                                    if (convertedDate && !isNaN(convertedDate.getTime())) {
                                                        // Formatar como DD/MM/YYYY
                                                        const day = String(convertedDate.getDate()).padStart(2, '0');
                                                        const month = String(convertedDate.getMonth() + 1).padStart(2, '0');
                                                        const year = convertedDate.getFullYear();
                                                        cellValue = `${day}/${month}/${year}`;
                                                    }
                                                } 
                                                // Se for string, formatar corretamente
                                                else if (typeof column === 'string' && column.includes('/')) {
                                                    const parts = column.split('/');
                                                    if (parts.length === 3) {
                                                        let first = parseInt(parts[0]);
                                                        let second = parseInt(parts[1]);
                                                        let third = parts[2];
                                                        
                                                        // Se first > 12, definitivamente é DD/MM
                                                        if (first > 12) {
                                                            // Já está DD/MM/YYYY - apenas formatar
                                                            cellValue = `${String(first).padStart(2, '0')}/${String(second).padStart(2, '0')}/${third.padStart(4, '0')}`;
                                                        } else {
                                                            // Ambigüidade: pode ser MM/DD ou DD/MM
                                                            // Converter para DD/MM/YYYY assumindo formato americano (MM/DD)
                                                            // e invertendo para brasileiro (DD/MM)
                                                            const year = third.length === 2 ? '19' + third : third.padStart(4, '0');
                                                            // Inverter: MM/DD -> DD/MM
                                                            cellValue = `${String(second).padStart(2, '0')}/${String(first).padStart(2, '0')}/${year}`;
                                                        }
                                                    }
                                                }
                                            } catch (error) {
                                                console.warn('Erro ao formatar data para visualização:', error);
                                            }
                                        }
                                        
                                        return <TableCell key={columnIndex}>{cellValue}</TableCell>;
                                    })
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };


    const handleCloseImport = async () => {
        try {
            history.push("/contacts");
        } catch (err) {
            toastError(err);
        }
    };

    const renderContent = () => {
        return (
            <div>
                <div className={classes.importOptions}>
                    <FormGroup row style={{width: '100%', display: 'flex', justifyContent: 'space-around'}}>
                        <FormControlLabel
                            control={
                                <Switch checked={validateContact} onChange={(event) => setValidateContact(event.target.checked)} color="primary"/>
                            }
                            label="Validar contatos no WhatsApp"
                        />
                    </FormGroup>
                </div>
                {renderXls()}
                <div className={classes.actions}>
                    {uploading && <div>Importando... Aguarde</div>}
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={uploading}
                        className={classes.buttonImport}
                        onClick={() => processImport()}
                    >
                        Importar contatos
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        disabled={uploading}
                        onClick={() => {
                            setRows(null);
                            setColumns(null);
                        }}
                    >
                        Cancelar
                    </Button>
                    {error && <div className={classes.error}>{error}</div>}
                </div>
            </div>
        );
    };

    const {getRootProps, getInputProps} = useDropzone({
        onDrop: onChangeFile,
        maxFiles: 1,
        accept: {
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'text/csv': ['.csv'],
            'text/plain': ['.txt'],
        }
    });

    return (
        <div style={{alignContent: "center"}}>
            {openingFile && (
                <div style={{
                    textAlign: 'center',
                    padding: '20px',
                    margin: '20px auto',
                    maxWidth: '500px'
                }}>
                    <CircularProgress/>
                    <p>Processando arquivo...</p>
                </div>
            )}
            {invalidFile && (
                <div style={{
                    padding: '15px',
                    margin: '15px auto',
                    maxWidth: '500px',
                    border: '1px solid #f5c6cb',
                    borderRadius: '8px',
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    textAlign: 'center'
                }}>
                    <h3>Arquivo inválido!</h3>
                    <p>O arquivo que você tentou importar não é válido. Por favor, tente novamente com um arquivo no formato correto.</p>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setInvalidFile(false);
                            setRows(null);
                            setColumns(null);
                        }}
                        style={{marginTop: '10px'}}
                    >
                        Tentar novamente
                    </Button>
                </div>
            )}
            {!invalidFile && rows && columns ? renderContent() : (
                !invalidFile && !openingFile && (
                    <>
                        <div
                            {...getRootProps()}
                            className="uploaderDrop"
                            style={{
                                borderRadius: 20,
                                maxWidth: 500,
                                margin: "20px auto",
                                border: "3px dotted #ddd",
                                padding: 20,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                flexDirection: "column",
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                backgroundColor: "#f9f9f9",
                                "&:hover": {
                                    backgroundColor: "#f0f0f0",
                                    borderColor: "#aaa",
                                }
                            }}
                        >
                            <img src={upload} height={200} alt="Upload"/>
                            <h5>Clique ou arraste um arquivo Excel</h5>
                            <p style={{color: "#e74c3c", fontWeight: "bold", textAlign: "center"}}>
                                * Formatos aceitos: xls, xlsx, csv, txt
                            </p>
                            <p style={{textAlign: "center", fontSize: "14px", marginTop: "10px", color: "#666"}}>
                                Para importar contatos, você deve ter pelo menos as colunas de nome e número de telefone.
                            </p>
                        </div>

                        <input {...getInputProps()} />

                        <div className={classes.backButtonContainer}>
                            <Button variant="contained" color="secondary" disabled={uploading} onClick={handleCloseImport}>
                                Voltar
                            </Button>
                        </div>
                    </>
                )
            )}
        </div>
    );
};

export default ContactImport;
